// app-shell-work-context-R04/R07; frontend-screens-app-shell-R09…R12.
// Effects for the shell's context; active selection is kept in entity reducers.
import { watch } from 'vue';
import type { Router, RouteLocationNormalized, LocationQueryRaw } from 'vue-router';
import { ElMessage } from 'element-plus';
import { useAuthStore } from '@/entities/auth';
import { useProjectsStore } from '@/entities/projects';
import { useFeaturesStore } from '@/entities/features';
import { resolveWorkContext } from '@/shared/api/work-context';
import { getAuthSessionGeneration } from '@/shared/api/client';
import { toApiError } from '@/shared/api/errors';
import { resolveApiErrorText } from '@/shared/lib';
import { i18n } from '@/shared/i18n';

export function installRestrictedWorkContext(router: Router): (addressChanged: boolean) => boolean {
  const auth = useAuthStore();
  const projects = useProjectsStore();
  const features = useFeaturesStore();
  let revision = 0;
  let resolving = false;
  let applying = false;
  let verifiedProject = '';
  let verifiedFeature = '';
  let settledAddress = '';
  let verifiedGeneration = getAuthSessionGeneration();
  let pendingSelection = '';
  let rejectedAddress = '';

  function restricted(route: RouteLocationNormalized): boolean {
    return auth.isAuthenticated && route.meta['projectScoped'] === true &&
      (!auth.hasPrivilege('metabridge.projects.view') ||
        (route.meta['featureScoped'] === true && !auth.hasPrivilege('metabridge.versions.view')));
  }
  function target(route: RouteLocationNormalized): { operation: string; featureScoped: boolean } | null {
    if (route.path === '/business-model' && auth.hasPrivilege('metabridge.model.view')) {
      return { operation: 'GET /api/v1/bm/classes', featureScoped: true };
    }
    if (route.path === '/business-model' && auth.hasPrivilege('metabridge.model.edit')) {
      return { operation: 'POST /api/v1/bm/classes', featureScoped: true };
    }
    if (route.path === '/mappings' && auth.hasPrivilege('metabridge.mappings.view')) {
      return { operation: 'GET /api/v1/projects/{project_id}/features/{feature_id}/mapping-dataset-counts', featureScoped: true };
    }
    if (route.path === '/mappings' && auth.hasPrivilege('metabridge.mappings.edit')) {
      return { operation: 'POST /api/v1/projects/{project_id}/features/{feature_id}/classes/{class_id}/mapping-datasets', featureScoped: true };
    }
    if (route.path === '/' && auth.hasPrivilege('metabridge.sources.view')) {
      return { operation: 'GET /api/v1/sources/{source_id}', featureScoped: false };
    }
    if (route.path === '/') {
      const actions = [
        ['metabridge.sources.edit', 'POST /api/v1/sources'],
        ['metabridge.sources.run', 'POST /api/v1/sources/{id}/sync'],
        ['metabridge.exploration.view', 'GET /api/v1/projects/{project_id}/sources/{source_id}/schema-notes'],
        ['metabridge.exploration.edit', 'PUT /api/v1/projects/{project_id}/sources/{source_id}/hidden-schemas'],
        ['metabridge.project_dictionaries.manage', 'GET /api/v1/projects/{project_id}/tags'],
      ] as const;
      const action = actions.find(([code]) => auth.hasPrivilege(code));
      if (action) return { operation: action[1], featureScoped: false };
    }
    // R10: preparing feature creation needs a verified project, even if no
    // subject section is allowed. This request never creates the feature.
    if (auth.hasPrivilege('metabridge.versions.edit')) {
      return { operation: 'POST /api/v1/projects/{project_id}/features', featureScoped: false };
    }
    return null;
  }
  function queryId(route: RouteLocationNormalized, key: string): string {
    const value = route.query[key];
    return typeof value === 'string' ? value : '';
  }
  function reflect(projectId: string, featureId: string, userChanged = false): void {
    const route = router.currentRoute.value;
    const mapping = route.meta['mappingScoped'] === true;
    const projectKey = mapping ? 'projectId' : 'project';
    const featureKey = mapping ? 'featureId' : 'feature';
    const query: LocationQueryRaw = { ...route.query };
    if (mapping && userChanged &&
      ((queryId(route, projectKey) && queryId(route, projectKey) !== projectId) ||
        (queryId(route, featureKey) && queryId(route, featureKey) !== featureId))) {
      delete query['classId']; delete query['dataset'];
    }
    if (projectId) query[projectKey] = projectId; else delete query[projectKey];
    if (featureId && route.meta['featureScoped'] === true) query[featureKey] = featureId;
    else delete query[featureKey];
    const location = { path: route.path, query, hash: route.hash };
    const address = router.resolve(location).fullPath;
    settledAddress = address;
    if (address !== route.fullPath) void router.replace(location);
  }

  router.beforeEach(to => {
    // A navigation invalidates all earlier responses, including a switch to
    // a route that does not need a resolver (app-shell-shell-routing-R12).
    revision += 1;
    resolving = false;
    if (restricted(to) && target(to) !== null && to.fullPath !== settledAddress) {
      // Block mounting/reads before the new URL becomes current. Keeping the
      // verified pair separately allows a live archived choice to survive.
      features.beginContextVerification();
    }
  });

  function reconcile(addressChanged: boolean): boolean {
    const route = router.currentRoute.value;
    if (verifiedGeneration !== getAuthSessionGeneration()) {
      verifiedGeneration = getAuthSessionGeneration();
      verifiedProject = ''; verifiedFeature = ''; settledAddress = '';
    }
    if (!restricted(route)) {
      if (features.contextPending) features.applyVerifiedContext(features.projectId, features.selectedFeatureId);
      // A choice verified by the permitted lists is live too. Losing a list
      // privilege must not reselect an already chosen archived feature (R04).
      if (auth.isAuthenticated && projects.projects.some(p => p.id === projects.selectedProjectId) &&
        features.projectId === projects.selectedProjectId &&
        features.selectorOptions.some(f => f.id === features.selectedFeatureId)) {
        verifiedProject = projects.selectedProjectId; verifiedFeature = features.selectedFeatureId;
      }
      return false;
    }
    const intent = target(route);
    if (applying) return true;
    if (resolving) {
      if (projects.selectedProjectId === pendingSelection) return true;
      revision += 1; resolving = false;
    }
    if (intent === null) {
      if (features.contextPending) features.applyVerifiedContext(features.projectId, features.selectedFeatureId);
      if (projects.projects.some(p => p.id === projects.selectedProjectId)) {
        reflect(projects.selectedProjectId, '');
      }
      return true;
    }
    const mapping = route.meta['mappingScoped'] === true;
    const projectKey = mapping ? 'projectId' : 'project';
    const featureKey = mapping ? 'featureId' : 'feature';
    const addressProject = queryId(route, projectKey);
    const addressFeature = queryId(route, featureKey);
    if (!addressChanged && route.fullPath === rejectedAddress && !features.selectedFeatureId) return true;
    const projectId = addressChanged ? addressProject || projects.selectedProjectId : projects.selectedProjectId || addressProject;
    let featureId = '';
    if (intent.featureScoped) {
      if (addressChanged) featureId = addressFeature || (projectId === verifiedProject ? verifiedFeature : '');
      else if (features.projectId === projectId) featureId = features.selectedFeatureId;
    }
    if (!projectId && route.fullPath === settledAddress) return true;
    // Creation responses are authoritative too (R12), including a feature
    // created while the current route only carries the project.
    const createdFeature = features.selectedFeatureId;
    if (!addressChanged && projects.selectedProjectId === projectId &&
      features.projectId === projectId && features.features.some(f => f.id === createdFeature)) {
      verifiedProject = projectId; verifiedFeature = createdFeature;
      reflect(projectId, createdFeature, true);
      return true;
    }

    // R04/R07: a verified live selection survives section changes and archive.
    if (projectId && projectId === verifiedProject &&
      (!intent.featureScoped || featureId === verifiedFeature) &&
      (featureId || !intent.featureScoped || route.fullPath === settledAddress)) {
      applying = true;
      features.applyVerifiedContext(projectId, verifiedFeature);
      applying = false;
      reflect(projectId, intent.featureScoped ? verifiedFeature : '');
      return true;
    }
    const requestRevision = ++revision;
    const generation = getAuthSessionGeneration();
    const grants = auth.privileges;
    const address = route.fullPath;
    const userChanged = !addressChanged && projectId !== addressProject;
    pendingSelection = projects.selectedProjectId;
    resolving = true;
    features.beginContextVerification();
    void resolveWorkContext({
      targetOperation: intent.operation,
      ...(projectId ? { projectId } : {}),
      ...(featureId ? { featureId } : {}),
    }).then(context => {
      if (requestRevision !== revision || generation !== getAuthSessionGeneration() ||
        auth.privileges !== grants || router.currentRoute.value.fullPath !== address) return;
      const selectedProject = context.projectId ?? '';
      const selectedFeature = context.featureId ?? '';
      pendingSelection = selectedProject;
      if (context.featureReplaced && mapping && route.query['classId'] !== undefined) {
        // frontend-class-mappings-R34: an explicit class link is never
        // silently redirected to a different feature's class.
        applying = true;
        features.applyVerifiedContext(selectedProject, '');
        projects.selectProject(selectedProject);
        applying = false;
        settledAddress = address;
        rejectedAddress = address;
        return;
      }
      verifiedProject = selectedProject; verifiedFeature = selectedFeature;
      applying = true;
      features.applyVerifiedContext(selectedProject, selectedFeature);
      projects.selectProject(selectedProject);
      applying = false;
      if (context.featureReplaced) ElMessage.warning(i18n.global.t('feature.notInLinkProject'));
      rejectedAddress = '';
      reflect(selectedProject, selectedFeature, userChanged || !addressChanged);
    }).catch((failure: unknown) => {
      if (requestRevision !== revision || generation !== getAuthSessionGeneration()) return;
      applying = true;
      pendingSelection = '';
      features.applyVerifiedContext('', '');
      projects.selectProject('');
      applying = false;
      // Existing dictionary refusal; no new empty-workspace message (R11).
      ElMessage.error(resolveApiErrorText(toApiError(failure), (key, params) =>
        i18n.global.te(key) ? i18n.global.t(key, params) : undefined));
      settledAddress = address;
      rejectedAddress = address;
    }).finally(() => { if (requestRevision === revision) resolving = false; });
    return true;
  }
  watch(() => auth.privileges, () => {
    revision += 1; resolving = false; settledAddress = '';
    reconcile(true);
  });
  return reconcile;
}
